import { HttpErrorResponse } from '@angular/common/http';
import {
  ErrorHandler,
  Injectable,
  Injector,
  NgZone,
  inject
} from '@angular/core';

import { ToastController } from '@gruposaltaedu/liv-design-system/components';
import { environment } from 'environments/environment';

import { getRemoteError } from '@app/shared/utils/get-remote-error.utils';

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandlerService implements ErrorHandler {
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);

  private readonly alertDomains = this.extractDomainsFromEnvironment();

  handleError(error: Error | HttpErrorResponse): void {
    if (!(error instanceof HttpErrorResponse)) {
      console.error('An unexpected error occurred:', error);
      return;
    }

    if (!this.shouldShowAlert(error.url || '')) {
      console.warn(
        `Error from ${error.url} does not match configured alert domains. No alert will be shown.`
      );
      return;
    }

    this.zone.run(() => {
      const toast = this.injector.get(ToastController);

      const { message } = getRemoteError(
        error,
        this.getGenericMessage(error.status)
      );

      toast.danger({
        title: 'Atenção',
        message
      });
    });
  }

  private getGenericMessage(status: number): string {
    if (status >= 500) {
      return 'Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.';
    }

    if (status === 401) {
      return 'Acesso não autorizado. Faça sign-in novamente.';
    }

    if (status === 403) {
      return 'Você não tem permissão para executar esta ação.';
    }

    if (status === 404) {
      return 'Recurso não encontrado.';
    }

    if (status >= 400 && status < 500) {
      return 'Requisição inválida. Verifique os dados e tente novamente.';
    }

    return 'Ocorreu um erro inesperado.';
  }

  private shouldShowAlert(url: string): boolean {
    if (!url) {
      return false;
    }

    try {
      const domain = new URL(url).hostname;
      return this.alertDomains.some(
        (alertDomain) => domain.endsWith(alertDomain) || domain === alertDomain
      );
    } catch {
      return false;
    }
  }

  private extractDomainsFromEnvironment(): string[] {
    const domains = new Set<string>();

    if (environment.apiUrl) {
      domains.add(this.extractDomain(environment.apiUrl));
    }

    return Array.from(domains);
  }

  private extractDomain(url: string): string {
    try {
      const domain = url.replace(/(https?:\/\/)?([^\/]+).*/i, '$2');
      return domain;
    } catch {
      return url;
    }
  }
}
